import { InputProps, keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Percent, { percentageValue } from "_shared/percentages/percent";
import * as Constants from "config";
import React from "react";
import { NumberFormatValues, NumericFormat, SourceInfo } from "react-number-format";
import { Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  fieldHasPendingChange,
  pendingChangeOutlinedFieldStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalInfoTooltip from "work/proposals/_views/proposal-info-tooltip";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ReviewerEditLockAdornment } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ComponentContainer = styled("div")(({ theme }) => ({
  alignContent: "center",
  display: "grid",
  height: "100%",
  justifyContent: "start",
  margin: theme.spacing(3),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));
const HighlightTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "locked" && prop !== "pendingChange",
})<{ active?: boolean; locked?: boolean; pendingChange?: boolean }>(
  ({ active, locked, pendingChange, theme }) => ({
    ...(pendingChange && pendingChangeOutlinedFieldStyles(theme)),
    ...(active && !locked && {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          animation: `${pulseBorder} 1.5s ease-in-out infinite`,
        },
      },
    }),
    ...(locked && {
      "& .MuiInputLabel-root.MuiInputLabel-shrink": {
        color: "warning.main",
      },
      "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
        color: "warning.main",
      },
      "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
        color: "warning.main",
      },
      "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
    }),
  })
);

const discountDescription =
  "You can share the Attorney Hub fees you incur from a project by establishing " +
  'an agreed upon "discount" from your vendor. 2.912% is the value to cover the cost.';

type DiscountTabProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  showPendingChangeHighlights?: boolean;
  negotiationId?: string;
  viewerScope?: string;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function DiscountTab(props: Readonly<DiscountTabProps>) {
  const {
    activeReviewField,
    proposalBuilder,
    disableEditing,
    fieldLockTooltip,
    showPendingChangeHighlights,
    negotiationId,
    viewerScope,
    onProposalBuilderUpdated,
  } = props;

  const proposalBuilderDiscount = Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount)
    ? 0
    : proposalBuilder.currentSpec.workAgreement.discount?.value ?? 0;
  const [editingDiscount, setEditingDiscount] = React.useState<number>();

  // The local edit is surrendered only once the builder reports the value that was published, never
  // in the same breath as publishing it. Doing both at once was a race: between the two the field
  // renders from whatever the builder last held, so when that propagation lost the race a typed
  // discount was silently replaced by the previous one — observed as `3.00%` persisting across 15
  // seconds of polling after `17.25` had been committed.
  //
  // Adjusting state during render rather than in an effect is React's documented way to reconcile
  // state with a changed prop: it re-renders before committing, so the stale value is never painted.
  if (editingDiscount !== undefined && editingDiscount === proposalBuilderDiscount) {
    setEditingDiscount(undefined);
  }

  const isEditing = editingDiscount !== undefined;
  const discount = isEditing ? editingDiscount : proposalBuilderDiscount;
  const pendingChangeFields = showPendingChangeHighlights
    ? proposalBuilder.sessionHistory?.flatMap((change) => change.field ? [change.field] : []) ?? []
    : [];
  const discountHasPendingChange = fieldHasPendingChange(pendingChangeFields, ProposalField.Discount);
  const lockAdornment =
    disableEditing && fieldLockTooltip ? <ReviewerEditLockAdornment title={fieldLockTooltip} /> : undefined;
  const lockedFieldSx = lockAdornment
    ? {
      "& .MuiInputLabel-root.MuiInputLabel-shrink": {
        color: "warning.main",
      },
      "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
        color: "warning.main",
      },
      "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
        color: "warning.main",
      },
      "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "warning.main",
      },
    }
    : undefined;

  function updateProposalDiscount(discountOverride?: number) {
    const committed = discountOverride ?? discount;
    // Hold the committed value locally until the builder comes back carrying it. The clear happens
    // in render, above, once the two agree.
    setEditingDiscount(committed);
    onProposalBuilderUpdated(proposalBuilder.setDiscount(new Percent(committed)));
  }

  /**
   * Handles the change event for the discount text field. Commits straight to the builder rather
   * than waiting for blur/Enter/Tab: Save and Submit read their enabled state off the builder, so a
   * discount that stayed purely local until the field lost focus left both disabled the moment a user
   * typed a value and reached straight for Save — a disabled button never receives the mousedown that
   * would otherwise blur the field and commit it, so nothing short of clicking elsewhere first could
   * ever unlock them (#4956).
   * @param {NumberFormatValues} values The new values
   * @param {SourceInfo} sourceInfo The source of the change
   */
  async function handleDiscountChange(values: NumberFormatValues, sourceInfo: SourceInfo) {
    if (sourceInfo.source === "prop") return;

    let updatedDiscount = (values.floatValue ?? 0) / 100;
    // Clamp to between 0 and 100%
    if (updatedDiscount < 0) updatedDiscount = 0;
    if (updatedDiscount > 1) updatedDiscount = 1;

    updateProposalDiscount(updatedDiscount);
  }

  function beginEditing() {
    if (isEditing) return;
    setEditingDiscount(proposalBuilderDiscount);
  }

  if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount)) {
    return maskedProposalFieldNotice(MaskedProposalField.Discount);
  }

  return (
    <ComponentContainer>
      <NumericFormat
        name="discount"
        label="Discount"
        active={Boolean(activeReviewField?.isEqualTo(ProposalField.Discount))}
        locked={Boolean(lockAdornment)}
        sx={lockedFieldSx}
        pendingChange={discountHasPendingChange}
        customInput={HighlightTextField}
        defaultValue={percentageValue(Constants.defaultDiscount)}
        allowNegative={false}
        value={percentageValue(discount)}
        suffix="%"
        type="text"
        thousandSeparator={false}
        decimalScale={2}
        fixedDecimalScale={true}
        disabled={disableEditing}
        isAllowed={(values) => {
          const { floatValue } = values;
          return floatValue == null || (floatValue >= 0 && floatValue <= 100);
        }}
        slotProps={{
          input: {
            startAdornment: lockAdornment,
            endAdornment: (
              <ProposalFieldHistoryButton
                field={ProposalField.Discount}
                negotiationId={negotiationId}
                viewerScope={viewerScope}
              />
            ),
          } as Partial<InputProps> & { active: boolean },
        }}
        onFocus={() => {
          beginEditing();
        }}
        onBlur={() => {
          updateProposalDiscount();
        }}
        onValueChange={handleDiscountChange}
        onKeyDown={(event) => {
          if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount)) return;

          if (event.key === KeyboardKey.Enter || event.key === KeyboardKey.Tab) {
            updateProposalDiscount();
          }
        }}
      />
      <HelperText>
        A discount of <b>3%</b> will cover subscription costs.
        <ProposalInfoTooltip title={discountDescription} />
      </HelperText>
    </ComponentContainer>
  );
}
